import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Blocks } from 'react-loader-spinner';
import { isSuperAdmin } from '../helpers/user';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const EDIT_ICON = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    className="bi bi-pencil-square"
    viewBox="0 0 16 16"
  >
    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
    <path
      fillRule="evenodd"
      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
    />
  </svg>
);

const DELETE_ICON = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    className="bi bi-trash-fill"
    viewBox="0 0 16 16"
  >
    <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z" />
  </svg>
);

const BASE_COLUMNS = [
  {
    headerName: 'Fiscal Year',
    valueGetter: (p) => p.data?.year?.fyname ?? 'N/A',
    minWidth: 110,
    pinned: 'left',
    lockPinned: true,
  },
  {
    headerName: 'Warehouse',
    valueGetter: (p) => p.data?.warehouse?.warehousename ?? 'N/A',
    minWidth: 140,
    pinned: 'left',
    lockPinned: true,
  },
  {
    headerName: 'Country',
    valueGetter: (p) => p.data?.warehouse?.country?.countryname ?? 'N/A',
    minWidth: 130,
  },
  {
    headerName: 'Product SAP Code / SKU',
    valueGetter: (p) => p.data?.product?.sapcode ?? 'N/A',
    minWidth: 170,
  },
  {
    headerName: 'Satellite',
    valueGetter: (p) => p.data?.satellite ?? p.data?.product?.satellite ?? 'N/A',
    minWidth: 110,
  },
  {
    headerName: 'Product',
    valueGetter: (p) => p.data?.product?.productdescription ?? 'N/A',
    minWidth: 220,
    flex: 1,
    pinned: 'left',
    lockPinned: true,
  },
  { headerName: 'Jul', field: 'july', minWidth: 90, valueGetter: (p) => p.data?.july ?? 'N/A' },
  { headerName: 'Aug', field: 'august', minWidth: 90, valueGetter: (p) => p.data?.august ?? 'N/A' },
  { headerName: 'Sep', field: 'september', minWidth: 90, valueGetter: (p) => p.data?.september ?? 'N/A' },
  { headerName: 'Oct', field: 'october', minWidth: 90, valueGetter: (p) => p.data?.october ?? 'N/A' },
  { headerName: 'Nov', field: 'november', minWidth: 90, valueGetter: (p) => p.data?.november ?? 'N/A' },
  { headerName: 'Dec', field: 'december', minWidth: 90, valueGetter: (p) => p.data?.december ?? 'N/A' },
  { headerName: 'Jan', field: 'january', minWidth: 90, valueGetter: (p) => p.data?.january ?? 'N/A' },
  { headerName: 'Feb', field: 'february', minWidth: 90, valueGetter: (p) => p.data?.february ?? 'N/A' },
  { headerName: 'Mar', field: 'march', minWidth: 90, valueGetter: (p) => p.data?.march ?? 'N/A' },
  { headerName: 'Apr', field: 'april', minWidth: 90, valueGetter: (p) => p.data?.april ?? 'N/A' },
  { headerName: 'May', field: 'may', minWidth: 90, valueGetter: (p) => p.data?.may ?? 'N/A' },
  { headerName: 'Jun', field: 'june', minWidth: 90, valueGetter: (p) => p.data?.june ?? 'N/A' },
];

/**
 * Reusable AG Grid table that displays warehouse inventory rows.
 *
 * The optional "Options" column (edit / delete / copy) is only rendered when
 * `showActions` is true, so pages that just need a read-only listing (e.g. the
 * LEO inventory page) can reuse the same table without wiring up modals.
 */
export default function InventoriesTable({
  inventories = [],
  loading = false,
  showActions = true,
  onEdit,
  onDelete,
  onCopy,
  editModalTarget = '#exampleModal2',
  copyModalTarget = '#exampleModal1',
  height = '700px',
  showFilters = false,
  fiscalYearValue,
  onFiscalYearChange,
  onWarehouseNameChange,
  onCountryNameChange,
  onProductIdChange,
  onProductDescriptionChange,
  onSearch,
  onClear,
}) {
  const actionsColumn = {
    headerName: 'Options',
    pinned: 'right',
    minWidth: 170,
    sortable: false,
    resizable: false,
    cellRenderer: (params) => {
      const dep = params.data;
      const disabled = !isSuperAdmin();
      if (!dep) return null;

      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
          <button
            type="button"
            disabled={disabled}
            className="btn btn-light p-0"
            data-bs-toggle="modal"
            data-bs-target={editModalTarget}
            onClick={() => onEdit && onEdit(dep)}
          >
            {EDIT_ICON}
          </button>

          <button
            type="button"
            disabled={disabled}
            className="btn btn-light p-0"
            onClick={() => onDelete && onDelete(dep.inventoryid)}
          >
            {DELETE_ICON}
          </button>

          <button
            type="button"
            disabled={disabled}
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target={copyModalTarget}
            onClick={() => (onCopy ? onCopy(dep) : onEdit && onEdit(dep))}
            style={{ height: '28px', fontSize: '12px' }}
          >
            Copy
          </button>
        </div>
      );
    },
  };

  const columnDefs = showActions ? [...BASE_COLUMNS, actionsColumn] : BASE_COLUMNS;

  const filtersBar = showFilters ? (
    <div
      className="d-flex flex-row flex-wrap"
      style={{ gap: '10px', padding: '8px 0', alignItems: 'stretch' }}
    >
      <div className="d-flex flex-column align-items-center" style={{ minWidth: 120 }}>
        <div style={{ fontSize: '12px', textAlign: 'center', height: '16px', lineHeight: '16px' }}>
          Fiscal Year
        </div>
        <div className="d-flex flex-row align-items-center justify-content-center">
          <input
            className="form-control"
            style={{ width: '70px', height: '30px', fontSize: '12px', marginTop: '4px' }}
            onChange={onFiscalYearChange}
            value={fiscalYearValue}
            placeholder="Filter"
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-center" style={{ minWidth: 140 }}>
        <div style={{ fontSize: '12px', textAlign: 'center', height: '16px', lineHeight: '16px' }}>
          Warehouse
        </div>
        <div className="d-flex flex-row align-items-center justify-content-center">
          <input
            className="form-control"
            style={{ width: '120px', height: '30px', fontSize: '12px', marginTop: '4px' }}
            onChange={onWarehouseNameChange}
            placeholder="Filter"
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-center" style={{ minWidth: 140 }}>
        <div style={{ fontSize: '12px', textAlign: 'center', height: '16px', lineHeight: '16px' }}>
          Country
        </div>
        <div className="d-flex flex-row align-items-center justify-content-center">
          <input
            className="form-control"
            style={{ width: '120px', height: '30px', fontSize: '12px', marginTop: '4px' }}
            onChange={onCountryNameChange}
            placeholder="Filter"
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-center" style={{ minWidth: 180 }}>
        <div style={{ fontSize: '12px', textAlign: 'center', height: '16px', lineHeight: '16px' }}>
          Product SAP Code / SKU
        </div>
        <div className="d-flex flex-row align-items-center justify-content-center">
          <input
            className="form-control"
            style={{ width: '150px', height: '30px', fontSize: '12px', marginTop: '4px' }}
            onChange={onProductIdChange}
            placeholder="Filter"
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-center" style={{ minWidth: 240 }}>
        <div style={{ fontSize: '12px', textAlign: 'center', height: '16px', lineHeight: '16px' }}>
          Product
        </div>
        <div className="d-flex flex-row align-items-center justify-content-center">
          <input
            className="form-control"
            style={{ width: '200px', height: '30px', fontSize: '12px', marginTop: '4px' }}
            onChange={onProductDescriptionChange}
            placeholder="Filter"
          />
        </div>
      </div>

      <div
        className="d-flex flex-row align-items-end justify-content-center"
        style={{ gap: '6px', paddingTop: '16px' }}
      >
        <button
          type="button"
          className="btn btn-primary"
          style={{ fontSize: '12px', height: '30px' }}
          onClick={onSearch}
          disabled={loading}
        >
          Search
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: '12px', height: '30px' }}
          onClick={onClear}
          disabled={loading}
        >
          Clear
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {filtersBar}
      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '40vh',
            width: '150vh',
          }}
        >
          <Blocks
            visible={true}
            height="100"
            width="100"
            ariaLabel="blocks-loading"
            wrapperStyle={{}}
            wrapperClass="blocks-wrapper"
          />
          <div style={{ marginLeft: '10px' }}>Loading...</div>
        </div>
      ) : (
        <div className="ag-theme-alpine" style={{ width: '100%', height }}>
          <AgGridReact
            rowData={inventories}
            getRowId={(params) => String(params.data?.inventoryid)}
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: false,
            }}
            columnDefs={columnDefs}
          />
        </div>
      )}
    </>
  );
}
